<template>
  <div
    ref="cont"
    v-click-outside="() => close()"
    class="trasesorvw_container"
    tabindex="0"
    @keydown.stop.enter="handleEnter"
    @keydown.stop.esc="close"
    @click="close"
  >
    <div
      class="trainer-session-overview trasesorvw"
      :style="`--clr-score: ${trainerScoreGroup.clr}`"
    >
      <div class="trasesorvw_back anim-fade-in"></div>

      <div
        class="trasesorvw_inner anim-fade-in_slow no-scroll-track"
        @click.stop
      >
        <div class="trasesorvw_close icon_btn pa-2 mdi mdi-close csst">
          <div
            class="csst_content text-nowrap"
            style="top: 3.5rem"
            v-html="$t('common.close_key', { key: 'Esc' })"
          ></div>
        </div>
        <div
          class="trasesorvw_message-cont"
          style="grid-gap: 3rem"
        >
          <div class="trasesorvw_message-icon-cont">
            <i
              :class="trainerScoreGroup.icon"
              class="mdi mr-8 text-mega line-height_1"
              :style="`color: var(--clr-score)`"
            ></i>
            <div>
              <div class="trasesorvw_message text-big weight_700">
                {{ message }}
              </div>
              <div class="clr-grey weight_600 text-semi-large uppercases line-height_1s">
                {{ $tc('messages.trainer_hands', stats.total_hands) }}
              </div>
            </div>
          </div>
          <div class="trasesorvw_total-score">
            <ProgressRing
              :max="gtoScoreNrm"
              :maxCircle="1"
              :getClr="num => trainerScoreGroup.clr"
              style="--stroke-width: 0.5rem; --size: 9rem; --clr-stroke-back: var(--clr-black-1)"
            >
              <template #center>
                <div class="d-flex f-center f-column">
                  <div class="text-small weight_00 clr-grey uppercase">
                    {{ $tc('common.gto_score') }}
                  </div>
                  <div class="progress-ring_num">{{ numFixed(gtoScore * 100) }}%</div>
                </div>
              </template>
            </ProgressRing>
          </div>
        </div>

        <div
          class="trasesorvw_charts"
          style=""
        >
          <PracticeStatMovesChart
            :items="moves"
            style="
              --clr-back: transparent;
              width: clamp(16rem, 23.3rem, 23.3rem);
              padding-right: 1.75rem;
            "
          />
          <div
            class="position-relative text-nowrap trasesorvw_charts_mistakes"
            style="display: grid; grid-gap: 0.25rem; grid-template-columns: 1fr"
          >
            <div
              v-for="(row, index) in mistakes"
              :key="index"
              class="f-column text-normal"
            >
              <div class="clr-grey weight_500 position-relative text-medium">
                {{ row.name }}
                <span
                  class="csst position-absolutex ml- text-normsal"
                  @mouseenter="fl_positionTooltip"
                >
                  <i
                    class="mdi mdi-help-circle-outline text-small"
                    style="color: var(--clr-grey-2)"
                  ></i>
                  <span
                    class="csst_content clr-white"
                    style="position: fixed"
                    v-html="row.tooltip"
                  >
                  </span>
                </span>
              </div>
              <div class="clr-white weight_700 position-relative">
                {{ row.value }} {{ row.unit || '' }}
              </div>
            </div>
          </div>
        </div>

        <div class="trasesorvw_hands">
          <div
            v-for="hand in handHighlights"
            :key="hand.uid"
            class="trasesorvw_hands_hand"
            @click="handleHandClick(hand)"
          >
            <div class="gw_btn gw_btn_text gw_btn-v1 gw_btn_outline">
              {{ hand.text }}
            </div>
            <CardRow
              :hasAnimation="false"
              class="ab_street_cards card-row_card_distant"
              size="small"
              :cards="hand.query_trainer_history.hand_tmp"
            />
          </div>

          <div
            id="tra_ovrw_history"
            class="gw_btn gw_btn_text gw_btn-v1 gw_btn_outline"
            @click="showHistory()"
          >
            <i class="mdi mdi-history text-semi-large mr-2"></i>
            {{ mobile ? $t('btn.history') : $t('btn.show_history') }}
          </div>
        </div>
      </div>
      <div class="trasesorvw_btns bt-grsey anim-fade-in_slow">
        <div
          v-for="(btn, idx) in btns"
          :id="btn.id"
          :key="btn.icon"
          class="trasesorvw_btns_btn flex-1 gw_btn gw_btn-v1"
          :class="btns.length - 1 === idx ? 'gw_btn_primary' : 'gw_btn_text'"
          @click="btn.fn()"
        >
          <i
            class="mdi trasesorvw_btns_btn_icon"
            :class="btn.icon"
          ></i>
          {{ mobile ? btn.text_abbr : btn.text }}
        </div>
      </div>

      <div
        v-if="showPog"
        class="anim-slide-fade-in-timed trasesorvw_pog trasesorvw_pog1"
      >
        <Pog />
      </div>
      <div
        v-if="showPog"
        class="anim-slide-fade-in-timed trasesorvw_pog trasesorvw_pog2"
        style="--slide-in-x: -2rem"
      >
        <Pog />
      </div>
    </div>
  </div>
</template>

<script>
import { nextTick } from 'vue'
import CardRow from '@/components/CardRow/CardRow'
import format_mix from '@/mixins/format_mix'
import ProgressRing from '@/components/lib/ProgressRing'
import PracticeStatMovesChart from '@/components/PracticeHandsTotals/PracticeStatMovesChart'
import { getTrainerScoreGroup } from '@/helpers/trainer'
import { getCorrectnessActionGroups } from '@/helpers/constants'
import { getPercentageOfMax } from '@/helpers/helpers'
import float_mix from '@/mixins/float_mix'
import Pog from '@/assets/pog.svg'

export default {
  name: 'TrainerSessionOverview',
  components: {
    ProgressRing,
    Pog,
    PracticeStatMovesChart,
    CardRow,
  },
  mixins: [format_mix, float_mix],
  props: {
    item: Object,
    isCurrent: Boolean,
  },
  emits: ['repeat:session', 'show:hand', 'show:history', 'end:session', 'close'],
  data() {
    return {
      blunders: 0.2,
      okMoves: 0.8,
    }
  },
  mounted() {
    nextTick(() => {
      this.setFocus()
    })
  },
  methods: {
    handleEnter() {
      this.btns[this.btns.length - 1].fn()
    },

    repeatSession() {
      this.$emit('repeat:session', this.item)
    },
    handleHandClick(hand) {
      this.$emit('show:hand', hand)
    },
    showHistory() {
      this.$emit('show:history')
    },
    endSession() {
      this.$emit('end:session', this.item)
    },
    close() {
      this.$emit('close')
    },
    setFocus() {
      // console.log('focusPanel', this.$refs.panel, bool)
      if (this.$refs.cont) {
        this.$refs.cont.focus()
      }
    },
    getImage(img) {
      return new URL(`/src/assets/img/${img}`, import.meta.url).href
    },
  },
  computed: {
    message() {
      let res = this.trainerScoreGroup.texts
      res = res[Math.floor(res.length * Math.random())] || res[0]
      res = this.$t(`messages.${res}`)
      return res
    },
    handHighlights() {
      // console.log(this.item)
      const sessionHands = this.item?.session_state?.session_finished_hands_uids || []
      const allHands = this.$store.getters.history_hands
      const hands = allHands.filter(i => sessionHands.includes(i.uid))
      const scores = hands.map(i => i.avg_gto_score)
      const min = Math.min(...scores)
      const max = Math.max(...scores)
      // console.log(scores, hands, sessionHands)

      let res = [null, null]
      res[0] = hands.find(hh => hh.avg_gto_score === min)
      res[1] = hands.find(hh => hh.avg_gto_score === max && hh.uid !== res[0].uid)
      res[0] = {
        ...(res[0] ? JSON.parse(res[0].stringified_hand) : {}),
        text: this.$t('common.worst_hand'),
      }
      res[1] = {
        ...(res[1] ? JSON.parse(res[1].stringified_hand) : {}),
        text: this.$t('common.best_hand'),
      }
      // if (scores.length === 1) {
      //   res = [res[0]]
      // }
      res = res.filter(i => i?.uid)
      // console.log(res)
      return res
    },
    showPog() {
      if (this.mobile) return false
      if (this.stats.total_hands >= 20 && this.stats.avg_gto_score >= 0.945) {
        return true
      }
      return false
    },
    btns() {
      const sessionFinished = this.item.session_state.session_finished
      // console.log(this.item)
      const res = [
        {
          icon: 'mdi-chevron-double-right',
          text: this.$t('btn.continue_session'),
          // text: this.$t('btn.continue'),
          text_abbr: this.$t('btn.continue'),
          fn: this.close,
          visible: this.isCurrent && !sessionFinished,
          id: 'tra_ovrw_continue',
        },
        {
          icon: 'mdi-repeat',
          text: this.$t('btn.repeat_session'),
          text_abbr: this.$t('btn.repeat'),
          fn: this.repeatSession,
          visible: sessionFinished ? true : !this.isCurrent,
          id: 'tra_ovrw_repeat',
        },
        {
          icon: 'mdi-close',
          text: this.$t('btn.end_session'),
          text_abbr: this.$t('btn.end'),
          fn: this.endSession,
          visible: this.isCurrent,
          id: 'tra_ovrw_end',
        },
      ].filter(i => i.visible)
      // console.log(this.isCurrent)
      return res
    },
    mobile() {
      return this.$store.getters.globals_mobile
    },
    stats() {
      return this.item.stats_session
    },
    trainerScoreGroup() {
      return getTrainerScoreGroup(this.stats.gto_score_per_hand)
      // return getTrainerScoreGroup(this.stats.avg_gto_score)
    },
    gtoScore() {
      return this.stats?.gto_score_per_hand || 0
      // return this.stats?.avg_gto_score || 0
    },
    gtoScoreNrm() {
      const score = this.stats?.gto_score_per_hand
      // const score = this.stats?.avg_gto_score
      // let res = score > 0 ? (score + 1) / 2 : 0
      const res = score > 0 ? score : 0
      // console.log(score, res)
      // res = 0
      // res = 0.25
      // res = 0.5
      // res = 0.75
      // res = 1
      // console.log(score, res)
      // ret
      return res
    },
    moves() {
      const groups = getCorrectnessActionGroups()
      const rows = [
        {
          id: 'best_moves',
          name: this.$t(`practice.stats.bests`),
          group: groups.best_move,
        },
        {
          id: 'correct_moves',
          name: this.$t(`practice.stats.corrects`),
          group: groups.correct_move,
        },
        {
          id: 'inaccuracy_moves',
          name: this.$t(`practice.stats.inaccuracy`),
          group: groups.inaccuracy,
        },
        {
          id: 'wrong_moves',
          name: this.$t(`practice.stats.wrongs`),
          group: groups.wrong_move,
        },
        {
          id: 'blunder_moves',
          name: this.$t(`practice.stats.blunders`),
          group: groups.blunder,
        },
      ]
      const {stats} = this
      // const moves = stats.total_moves

      const movesValues = rows.map(dd => stats[dd.id])
      const max = Math.max(...movesValues)
      // console.log(stats)
      // debugger
      const res = rows.map(row => ({
        ...row,
        value: this.numKtoM(stats[row.id] || 0, 1000, 1),
        value_percent_scaled: getPercentageOfMax({
          max,
          value: stats[row.id],
        }),
        // }))
      }))
      // .filter(i => i.value)
      // console.log(res)
      return res
    },
    mistakes() {
      let res = [
        {
          id: 'total_ev_loss',
          decimal: 1,
        },
        {
          id: 'average_ev_loss',
          decimal: 2,
        },
      ]
      res = res.map(i => {
        const r = {
          name: this.$t(`practice.stats.${i.id}`),
          tooltip: this.$t(`tooltips.${i.id}`),
          value: this.numFixed(this.stats[i.id], i.decimal),
        }
        // debugger
        return r
      })
      return res
    },
  },
}
</script>
<style lang="scss" scoped>
@keyframes maxIn {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

.trasesorvw {
  position: absolute;
  top: 0;
  z-index: 4;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  &_container {
    height: 100%;
    width: 100%;
    display: flex;
    justify-content: center;
    position: absolute;
    --clr-back: var(--clr-black-0);
    --clr-score: transparent;
    top: 0;
  }

  &:before {
    content: '';
    position: absolute;
    background: radial-gradient(var(--clr-black-0) 10%, transparent);
    height: 300vh;
    width: 150vw;
    animation: maxIn 0.44s;
    opacity: 1;
  }
  &_close {
    position: absolute;
    top: 0;
    right: -4rem;
    font-size: 2rem;
    z-index: 5 !important;
  }
  &_back {
    position: fixed;
    top: 0;
    height: 100%;
    width: 100%;
    z-index: 1;
    --back-to: rgb(0, 0, 0);
    --back-from: rgba(0, 0, 0, 0.25);
    background: linear-gradient(180deg, var(--back-from), var(--back-to));
  }
  &_inner {
    padding: 1rem;
    border: solid 1px var(--clr-black-2);
    background: rgb(0, 0, 0, 0.5);

    border-radius: 8px;
    padding: 4rem;

    min-height: 0;
    max-height: var(--vh-6);
    overflow-y: auto;
    overflow-x: hidden;
    margin: 4rem 0 0 0;
    position: relative;
    display: flex;
    flex-direction: column;
    z-index: 2;
    flex: 1;
  }
  &_message-icon-cont {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &_message-cont {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &_message {
    text-transform: uppercase;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &_charts {
    display: flex;
    margin: 2.5rem 0 0 0;
    grid-gap: 0.75rem;
    &_mistakes {
      padding-left: 2.5rem;
      top: 4px;
      position: relative;
      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        width: 1px;
        background: var(--clr-black-3);
      }
    }
  }
  &_hands {
    flex-wrap: wrap;
    // align-items: center;
    // justify-content: center;
    display: flex;
    grid-gap: 2rem;
    margin-top: 3rem;
    &_hand {
      display: flex;
      grid-gap: 1rem;
    }
  }

  &_btns {
    display: flex;
    position: relative;
    white-space: nowrap;
    grid-gap: 0.25rem;
    width: 100%;
    max-width: 28rem;
    margin: 2rem 0 0 0;
    z-index: 4;
    &_btn {
      // }
      width: 100%;
      flex: 1;
      &_icon {
        margin: 0 0.25rem 0 0;
        font-size: 1.25rem;
        line-height: 1;
        position: relative;
      }
    }
  }
}

// .trasesorvw_pog {
//   z-index: 1;
//   position: fixed;
//   filter: brightness(55%);
//   height: 55.5rem;
//   svg {
//     width: 100%;
//     height: 100%;
//   }
// }
// .trasesorvw_pog1 {
//   right: -20rem;
// }
// .trasesorvw_pog2 {
//   left: -20rem;
//   top: 8rem;
//   svg {
//     transform: scaleX(-1);
//   }
// }

.trasesorvw_pog {
  z-index: 1;
  position: fixed;
  filter: brightness(55%);
  height: clamp(24rem, 55.5rem, 50vw);
  svg {
    width: 100%;
    height: 100%;
  }
}
.trasesorvw_pog1 {
  left: 64%;
}
.trasesorvw_pog2 {
  right: 64%;
  top: 10%;
  svg {
    transform: scaleX(-1);
  }
}

@media (max-width: 1023px) {
  .trasesorvw {
    &_inner {
      padding: 2rem;
    }
    &_btns {
      display: flex;
      position: fixed;
      bottom: calc(var(--glo-footer-height) + var(--safe-area-inset-bottom));
      background: var(--clr-black-1);
      z-index: 2;
      left: 0;
      width: 100vw;
      min-width: 100vw;
      margin: 0;
      white-space: normal;
      padding: 0.5rem;
    }
    &_charts {
      flex-wrap: wrap;
      &_mistakes {
        padding-left: 0;
        top: 0;
        &::before {
          display: none;
        }
      }
    }
  }
}
@media (max-width: 620px) {
  .trasesorvw {
    width: 100vw;
    max-width: 100vw;
    &_inner {
      margin: 0;
      border: none;
      width: 100vw;
      max-width: 100vw;
    }
    &_message-cont {
      flex-direction: column-reverse;
      align-items: flex-start;
    }
    &_message-icon-cont {
      justify-content: flex-start;
    }
  }
  .trasesorvw_pog {
    height: clamp(12rem, 55.5rem, 60vw);
    filter: brightness(88%);
  }
  .trasesorvw_pog1 {
    right: -18%;
    left: unset;
    top: 1rem;
  }
  .trasesorvw_pog2 {
    display: none;
  }
}
</style>
